import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paginate } from '../utils/Pagination';
import TransactionStatusPage from './TransactionStatusPage';
import { handleSearchChange, handleFilterTypeChange, filterTransactions } from '../utils/searchAndFilter';
import '../styles/dashboard.css';

const TransactionHistoryGrid = ({ transactions, onTransactionStatus, fetchTransactions }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const recordsPerPage = parseInt(process.env.REACT_APP_RECORDS_PER_PAGE, 10) || 10; // Default to 10 if env is undefined
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('customerName');
    const [selectedMessageGroupId, setSelectedMessageGroupId] = useState(null);
    const [selectedDocumentIdentifier, setSelectedDocumentIdentifier] = useState(null);
    const[selectedDocumentType, setSelectedDocumentType]=useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Ensure we log the transactions passed in for debugging
    useEffect(() => {
        console.log("All Transactions:", transactions);
    }, [transactions]);

    const filteredTransactions = filterTransactions(transactions, searchQuery, filterType);

    // Log the filtered transactions for debugging
    useEffect(() => {
        console.log("Filtered Transactions:", filteredTransactions);
    }, [filteredTransactions]);

    const totalPages = Math.ceil(filteredTransactions.length / recordsPerPage);
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredTransactions.slice(indexOfFirstRecord, indexOfLastRecord);

    // Log the current records to verify the slice logic
    useEffect(() => {
        console.log("Current Page:", currentPage);
        console.log("Records per Page:", recordsPerPage);
        console.log("Current Records:", currentRecords);
    }, [currentPage, filteredTransactions]);

    const nextPage = () => setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    const prevPage = () => setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    const setPage = (pageNumber) => setCurrentPage(pageNumber);

    const renderPageNumbers = Paginate(totalPages, currentPage, setPage);

    const downloadExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredTransactions);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
        XLSX.writeFile(wb, `transactions_${new Date().toISOString()}.xlsx`);
    };

    const handleTransactionStatusClick = (transaction) => {
        setSelectedMessageGroupId(transaction?.messageGroupId);
        setSelectedDocumentType(transaction?.documentType);
        setSelectedDocumentIdentifier(transaction?.documentIdentifier);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedMessageGroupId(null);
        setSelectedDocumentType(null);
    };

    const handleBackClick = () => {
        if (location.state?.from === 'TransactionHistory') {
            navigate('./transactionHistoryTab');
        } else {
            navigate(-1);
        }
    };

    return (
        <div>
            <div className="toolbar">
                <button onClick={handleBackClick} className="back-button">Back</button>
                <div className="search-container">
                    <button onClick={fetchTransactions} className="fetch-button">Search/Filter Transactions</button>
                    <select
                        value={filterType}
                        onChange={handleFilterTypeChange(setFilterType, setSearchQuery)}
                        className="filter-select"
                    >
                        <option value="customerName">Customer Name</option>
                        <option value="documentIdentifier">Document Identifier</option>
                        <option value="documentType">Document Type</option>
                        <option value="documentReferenceType">Document Reference Type</option>
                    </select>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange(setSearchQuery, setCurrentPage)}
                        placeholder={`Search by ${filterType.replace(/([A-Z])/g, ' $1').trim()}`}
                        className="search-input"
                    />
                </div>
                <button onClick={downloadExcel} className="download-button">Download to Excel</button>
            </div>

            <div className="transaction-grid">
                <div className="grid-header">
                    <div className="header-cell">Customer Name</div>
                    <div className="header-cell">Document Identifier</div>
                    <div className="header-cell">Document Type</div>
                    <div className="header-cell">Document Reference Type</div>
                    <div className="header-cell">DateTime</div>
                    <div className="header-cell">Status</div>
                </div>
                {currentRecords.length > 0 ? (
                    currentRecords.map((transaction, index) => (
                        <div className="grid-row" key={index}>
                            <div className="grid-cell">{transaction.customerName}</div>
                            <div className="grid-cell">{transaction.documentIdentifier}</div>
                            <div className="grid-cell">{transaction.documentType}</div>
                            <div className="grid-cell">{transaction.documentReferenceType}</div>
                            <div className="grid-cell">{transaction.DateTime}</div>
                            <div className="grid-cell">
                                <button
                                    className='status-button'
                                    onClick={() => handleTransactionStatusClick(transaction)}
                                    aria-label={`View status for ${transaction.documentIdentifier}`}
                                >
                                    View Status
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-records">No transactions available.</div>
                )}
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button onClick={prevPage} disabled={currentPage === 1} aria-label="Previous page">Back</button>
                    {renderPageNumbers()}
                    <button onClick={nextPage} disabled={currentPage === totalPages} aria-label="Next page">Next</button>
                </div>
            )}

            {isModalOpen && selectedMessageGroupId && selectedDocumentIdentifier && (
                <div className="modal">
                    <div className="modal-content">
                        <TransactionStatusPage
                            messageGroupId={selectedMessageGroupId}
                            documentType={selectedDocumentType}
                            referenceIdentifier={selectedDocumentIdentifier}
                        />
                        <button className="modal-close-button" onClick={handleCloseModal}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionHistoryGrid;
